import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { useCallback } from "react"
import { CampaignForm } from "@/components/seller-promotions/campaign-form"
import {
	type CampaignFormValues,
	toUpdateCampaignInput,
} from "@/components/seller-promotions/campaign-form.helpers"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useAdminRoles } from "@/hooks/use-admin-roles"
import {
	useSellerPromotionCampaign,
	useUpdateSellerPromotion,
} from "@/queries/marketplace"

export const Route = createFileRoute("/seller-promotions/$campaignId/edit")({
	component: EditSellerPromotionPage,
})

function EditSellerPromotionPage() {
	const { campaignId } = Route.useParams()
	const navigate = useNavigate()
	const { campaign, isLoading, error } = useSellerPromotionCampaign(campaignId)
	const updateMutation = useUpdateSellerPromotion()
	const { hasRole, isLoading: rolesLoading } = useAdminRoles()

	const handleSubmit = useCallback(
		async (values: CampaignFormValues) => {
			if (!campaign) {
				return
			}

			await updateMutation.mutateAsync(toUpdateCampaignInput(values, campaign))
			navigate({
				to: "/seller-promotions/$campaignId",
				params: { campaignId },
			})
		},
		[campaign, campaignId, updateMutation, navigate],
	)

	if (isLoading || rolesLoading) {
		return (
			<div className="flex flex-col gap-6 p-6">
				<Skeleton className="h-8 w-64" />
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<Skeleton className="h-64" />
					<Skeleton className="h-64" />
				</div>
			</div>
		)
	}

	if (error || !campaign) {
		return (
			<div className="flex flex-col items-start gap-3 p-6">
				<p className="text-bad">
					{error?.message || "The seller promotion could not be found."}
				</p>
				<Button variant="outline" asChild>
					<Link to="/seller-promotions">
						<ArrowLeft />
						Back to seller promotions
					</Link>
				</Button>
			</div>
		)
	}

	if (!hasRole(AdminRole.MarketplaceFinance)) {
		return (
			<div className="p-6">
				<p className="text-muted-foreground">
					Only admins with the finance role can change seller promotions.
				</p>
			</div>
		)
	}

	return (
		<CampaignForm
			mode="edit"
			campaign={campaign}
			onSubmit={handleSubmit}
			isSubmitting={updateMutation.isPending}
		/>
	)
}
